"use client";
import { NexusAddon, NexusProduct, NexusPromotion } from "@/types/nexus-product";
import { formatPromotionBadgeLabel } from "@/utils/promotions";
import {
  isNexusOptionAvailable,
  type NexusOptionGroup,
} from "./helpers/nexusVariants";

type OptionGroupsListProps = {
  groups: NexusOptionGroup[];
  product: NexusProduct | null;
  selectedOptions: Record<string, string>;
  selectedMultiOptions: Record<string, string[]>;
  pricingGroupIds: string[];
  unavailableLabel: string;
  onSelectOption: (groupId: string, optionId: string | null) => void;
  onToggleMultiOption: (
    groupId: string,
    optionId: string,
    requiredCount: number | null
  ) => void;
  getOptionPromotion?: (
    groupIndex: number,
    optionId: string
  ) => NexusPromotion | null;
};

export const OptionGroupsList = ({
  groups,
  product,
  selectedOptions,
  selectedMultiOptions,
  pricingGroupIds,
  unavailableLabel,
  onSelectOption,
  onToggleMultiOption,
  getOptionPromotion,
}: OptionGroupsListProps) => (
  <>
    {groups.map((group, groupIndex) => {
      if (group.multiSelect) {
        const selectedIds = selectedMultiOptions[group.groupId] ?? [];
        const requiredCount = group.requiredCount ?? 0;

        return (
          <div key={group.groupId}>
            <h4 className="font-medium text-dark text-sm">
              {group.groupName} (do wyboru {requiredCount} gratis!):
            </h4>
            {group.groupDescription && (
              <p className="text-dark-4 text-xs mt-3">
                {group.groupDescription}
              </p>
            )}
            <div className="flex items-center flex-wrap gap-2">
              {group.options.map((option) => {
                const isSelected = selectedIds.includes(option.id);
                const isAvailable =
                  !option.disabled &&
                  (isSelected || selectedIds.length < requiredCount);

                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={!isAvailable && !isSelected}
                    onClick={() =>
                      onToggleMultiOption(
                        group.groupId,
                        option.id,
                        group.requiredCount ?? null
                      )
                    }
                    className={`flex items-center justify-center px-3 py-1.5 min-w-[50px] text-sm font-normal transition-colors duration-200 border rounded-md ${
                      isSelected
                        ? "bg-blue text-white border-blue shadow-sm"
                        : isAvailable
                          ? "bg-white text-dark border-gray-3 hover:border-blue hover:text-blue"
                          : "bg-white text-gray-400 border-dashed cursor-not-allowed"
                    }`}
                  >
                    {option.name}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      return (
        <div key={group.groupId}>
          <h4 className="font-medium text-dark text-sm">{group.groupName}:</h4>
          {group.groupDescription && (
            <p className="text-dark-4 text-xs mt-3">{group.groupDescription}</p>
          )}
          <div className="flex items-center flex-wrap gap-2">
            {group.options.map((option) => {
              const isSelected = selectedOptions[group.groupId] === option.id;
              const isAvailable =
                !option.disabled &&
                (group.hasDisplayWhen ||
                  (product != null &&
                    isNexusOptionAvailable(
                      option.id,
                      group.groupId,
                      selectedOptions,
                      product,
                      pricingGroupIds
                    )));
              const optionPromotion =
                (isAvailable || isSelected) && getOptionPromotion
                  ? getOptionPromotion(groupIndex, option.id)
                  : null;

              return (
                <div key={option.id} className="relative pt-3">
                  {!isAvailable && !isSelected && (
                    <span
                      className="absolute top-3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-[8px] font-semibold px-1 py-0.5 rounded-sm leading-none whitespace-nowrap w-fit"
                      style={{
                        backgroundColor: "#dc2626",
                        color: "#ffffff",
                      }}
                    >
                      {unavailableLabel}
                    </span>
                  )}
                  {optionPromotion && (
                    <span className="absolute top-3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-[8px] font-bold px-1 py-0.5 rounded-sm leading-none whitespace-nowrap w-fit bg-red text-white">
                      {formatPromotionBadgeLabel(optionPromotion)}
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={!isAvailable && !isSelected}
                    onClick={() =>
                      onSelectOption(
                        group.groupId,
                        isSelected ? null : option.id
                      )
                    }
                    className={`flex items-center justify-center px-3 py-1.5 min-w-[50px] text-sm font-normal transition-colors duration-200 border rounded-md ${
                      isSelected
                        ? "bg-blue text-white border-blue shadow-sm"
                        : isAvailable
                          ? "bg-white text-dark border-gray-3 hover:border-blue hover:text-blue"
                          : "bg-white text-gray-400 border-dashed cursor-not-allowed"
                    }`}
                    style={
                      !isAvailable && !isSelected
                        ? {
                            borderColor: "var(--color-gray-4)",
                            color: "#9ca3af",
                          }
                        : undefined
                    }
                  >
                    {option.name}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </>
);

type AddonsPanelProps = {
  title: string;
  className?: string;
  addons: NexusAddon[];
  selectedAddons: Record<string, boolean>;
  isOpen: boolean;
  priceOf: (addon: NexusAddon) => number;
  onToggleOpen: () => void;
  onToggleAddon: (addonId: string, checked: boolean) => void;
};

export const AddonsPanel = ({
  title,
  className = "mb-6",
  addons,
  selectedAddons,
  isOpen,
  priceOf,
  onToggleOpen,
  onToggleAddon,
}: AddonsPanelProps) => (
  <div
    className={`${className} border border-gray-3 rounded-lg overflow-hidden`}
  >
    <button
      type="button"
      onClick={onToggleOpen}
      className="w-full px-4 py-3 flex items-center justify-between bg-gray-1 hover:bg-gray-2 transition-colors"
    >
      <span className="font-medium text-dark text-sm">{title}</span>
      <svg
        className={`w-4 h-4 text-dark transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
    {isOpen && (
      <div className="p-3 space-y-2">
        {addons.map((addon) => {
          const effectivePrice = priceOf(addon);
          const isSelected = selectedAddons[addon.id] ?? false;
          return (
            <button
              key={addon.id}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              onClick={() => onToggleAddon(addon.id, !isSelected)}
              className={`w-full flex items-center justify-between p-3 rounded-lg border text-left cursor-pointer transition-all duration-150 select-none ${
                isSelected
                  ? "border-blue bg-blue/5"
                  : "border-gray-3 bg-white hover:border-gray-400"
              }`}
            >
              <span className="flex items-center gap-3">
                <span
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-150 ${
                    isSelected ? "border-blue bg-blue" : "border-gray-3 bg-white"
                  }`}
                >
                  {isSelected && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </span>
                <span className="text-sm font-medium text-dark">
                  {addon.name}
                </span>
              </span>
              <span className="text-sm font-semibold text-blue ml-4 flex-shrink-0">
                +{(effectivePrice / 100).toFixed(2)} zł
              </span>
            </button>
          );
        })}
      </div>
    )}
  </div>
);
